import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, Pencil, Trash, CheckCircle, ArrowClockwise, Calendar, Star } from "@phosphor-icons/react";

const PUBLISH = ["draft", "review", "published"];

export default function AdminJournal() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);   // slug
  const [form, setForm] = useState(null);
  const [customTopic, setCustomTopic] = useState("");

  async function load() {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/journal");
      setItems(data.items || []);
    } catch (e) {
      toast.error("Failed to load journal");
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function generateNow(topic) {
    if (generating) return;
    setGenerating(true);
    const t = (topic || "").trim();
    toast.info(t ? `Generating draft on "${t}" — 20–40s…` : "Generating a new journal draft — 20–40s…");
    try {
      const { data } = await api.post("/admin/journal/generate", t ? { topic: t } : {});
      toast.success(`Draft created: "${data.title}" (${data.word_count} words)`);
      setCustomTopic("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  }

  function openEdit(post) {
    setEditing(post.slug);
    setForm({
      title: post.title || "",
      excerpt: post.excerpt || "",
      body: post.body || "",
      category: post.category || "Market intelligence",
      hero_image: post.hero_image || "",
      seo_title: post.seo_title || "",
      seo_description: post.seo_description || "",
      publish_status: post.publish_status || "draft",
      featured: !!post.featured,
    });
  }

  async function save() {
    try {
      await api.put(`/admin/journal/${editing}`, form);
      toast.success("Journal post saved");
      setEditing(null);
      setForm(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  }

  async function publish(post) {
    try {
      await api.put(`/admin/journal/${post.slug}`, { publish_status: "published", published: true });
      toast.success(`Published "${post.title}"`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Publish failed");
    }
  }

  async function remove(post) {
    if (!confirm(`Delete "${post.title}"?`)) return;
    try {
      await api.delete(`/admin/journal/${post.slug}`);
      toast.success("Post deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  }

  const inputCls = "w-full border border-[#E5E7EB] px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:border-[#001588]";

  return (
    <div className="p-6 lg:p-10" data-testid="admin-journal">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs tracking-[0.24em] uppercase text-gray-500 mb-1">Content · Auto-generated daily 07:00 GST</div>
          <h1 className="font-serif text-3xl lg:text-4xl">Journal</h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={load} className="border border-[#E5E7EB] px-3 py-2 text-sm hover:border-[#001588]" data-testid="journal-refresh"><ArrowClockwise size={16} /></button>
          <button onClick={() => generateNow()} disabled={generating} className="flex items-center gap-2 bg-[#001588] text-white px-5 py-3 text-sm hover:bg-black transition-colors disabled:opacity-60" data-testid="journal-generate">
            <Sparkle size={16} weight={generating ? "fill" : "regular"} />
            {generating ? "Generating…" : "Generate new draft"}
          </button>
        </div>
      </div>

      <div className="bg-[#ffffff] border border-[#e5e7eb] p-5 mb-6 text-sm text-neutral-700">
        <div className="font-serif text-lg mb-1">How it works</div>
        Every day at <b>07:00 GST</b> the AI picks a fresh Dubai real-estate topic from the topic bank and drafts a 1,300–1,600-word editorial. All drafts land here as <b>unpublished</b> so you can review, tweak the copy, and hit <b>Publish</b> when ready. Published posts appear at <b>/journal</b> with full SEO markup (title, meta, canonical, JSON-LD Article schema).
      </div>

      {/* Custom-topic generator — writes on the admin's exact title instead
          of pulling from the automatic bank. Stays outside the daily rotation. */}
      <div className="bg-white border border-[#e5e7eb] p-5 mb-6" data-testid="journal-custom-topic">
        <div className="font-serif text-lg mb-1">Write on your own topic</div>
        <p className="text-xs text-neutral-500 mb-3">
          Type a title or brief and hit <b>Generate</b>. The AI treats it as an editorial brief and drafts a full 1,300–1,600-word article — same quality as the daily bank, on your exact topic. Lands as a draft; you review + publish.
        </p>
        <form
          onSubmit={(e) => { e.preventDefault(); if (customTopic.trim()) generateNow(customTopic); }}
          className="flex flex-col sm:flex-row gap-2"
        >
          <input
            value={customTopic}
            onChange={(e) => setCustomTopic(e.target.value)}
            placeholder="e.g. Palm Jebel Ali — 2026 investment case"
            className="flex-1 border border-[#E5E7EB] px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:border-[#001588]"
            data-testid="journal-custom-topic-input"
            disabled={generating}
          />
          <button
            type="submit"
            disabled={generating || !customTopic.trim()}
            className="flex items-center justify-center gap-2 bg-[#001588] text-white px-5 py-2 text-sm hover:bg-black transition-colors disabled:opacity-50"
            data-testid="journal-custom-topic-generate"
          >
            <Sparkle size={16} weight={generating ? "fill" : "regular"} />
            {generating ? "Generating…" : "Generate"}
          </button>
        </form>
      </div>

      {busy && <div className="text-neutral-500 text-sm mb-4">Loading…</div>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="journal-table">
          <thead>
            <tr className="border-b border-neutral-200 text-left text-xs uppercase tracking-wider text-neutral-500">
              <th className="py-3 px-3">Title</th>
              <th className="py-3 px-3 hidden md:table-cell">Category</th>
              <th className="py-3 px-3 hidden lg:table-cell">Words</th>
              <th className="py-3 px-3 hidden lg:table-cell">Source</th>
              <th className="py-3 px-3">Status</th>
              <th className="py-3 px-3 hidden md:table-cell">Created</th>
              <th className="py-3 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => {
              const wc = String(p.body || "").split(/\s+/).filter(Boolean).length;
              return (
                <tr key={p.id} className="border-b border-neutral-100 hover:bg-[#ffffff]/60" data-testid={`journal-row-${p.slug}`}>
                  <td className="py-3 px-3">
                    <div className="font-medium text-neutral-900 line-clamp-1 flex items-center gap-2">
                      {p.featured && <Star size={14} weight="fill" className="text-[#ffffff] flex-shrink-0" />}
                      {p.title}
                    </div>
                    <div className="text-xs text-neutral-500 line-clamp-1">/journal/{p.slug}</div>
                  </td>
                  <td className="py-3 px-3 hidden md:table-cell text-neutral-600">{p.category || "—"}</td>
                  <td className="py-3 px-3 hidden lg:table-cell text-neutral-600">{wc.toLocaleString()}</td>
                  <td className="py-3 px-3 hidden lg:table-cell">
                    {p.ai_generated ? <span className="text-[10px] bg-[#001588]/10 text-[#001588] px-2 py-0.5 uppercase tracking-wider">AI</span> : <span className="text-[10px] text-neutral-400">Manual</span>}
                  </td>
                  <td className="py-3 px-3">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${p.published ? "bg-green-100 text-green-800" : (p.publish_status === "review" ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800")}`}>
                      {p.published ? "published" : (p.publish_status || "draft")}
                    </span>
                  </td>
                  <td className="py-3 px-3 hidden md:table-cell text-neutral-500 text-xs">{(p.created_at || "").slice(0, 10)}</td>
                  <td className="py-3 px-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(p)} className="text-xs text-[#001588] hover:underline mr-3" data-testid={`journal-edit-${p.slug}`}><Pencil size={14} className="inline mr-1" />Edit</button>
                    {!p.published && (
                      <button onClick={() => publish(p)} className="text-xs text-green-700 hover:underline mr-3" data-testid={`journal-publish-${p.slug}`}><CheckCircle size={14} className="inline mr-1" />Publish</button>
                    )}
                    <button onClick={() => remove(p)} className="text-xs text-red-600 hover:underline" data-testid={`journal-delete-${p.slug}`}><Trash size={14} className="inline" /></button>
                  </td>
                </tr>
              );
            })}
            {!busy && items.length === 0 && (
              <tr><td colSpan={7} className="text-center py-14 text-neutral-500">No journal posts yet. Click <b>Generate new draft</b> to create your first AI-drafted article.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && form && (
        <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="w-full max-w-4xl bg-white p-6 lg:p-8 my-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl">Edit journal post</h2>
              <div className="text-xs text-neutral-500 flex items-center gap-1"><Calendar size={12} /> /journal/{editing}</div>
            </div>

            <div className="grid gap-4">
              <label>
                <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Title *</span>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="journal-title" />
              </label>
              <label>
                <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Excerpt / meta description</span>
                <textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows="2" className={inputCls} data-testid="journal-excerpt" />
              </label>
              <div className="grid sm:grid-cols-2 gap-4">
                <label>
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Hero image URL</span>
                  <input value={form.hero_image} onChange={(e) => setForm({ ...form, hero_image: e.target.value })} className={inputCls} data-testid="journal-hero" />
                </label>
                <label>
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Category</span>
                  <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="journal-category" />
                </label>
                <label>
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">SEO title</span>
                  <input value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} className={inputCls} data-testid="journal-seo-title" />
                </label>
                <label>
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">SEO description</span>
                  <input value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} className={inputCls} data-testid="journal-seo-desc" />
                </label>
                <label>
                  <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Publish status</span>
                  <select value={form.publish_status} onChange={(e) => setForm({ ...form, publish_status: e.target.value })} className={inputCls} data-testid="journal-status">
                    {PUBLISH.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label className="flex items-center gap-2 mt-6">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="journal-featured" />
                  <span className="text-sm">Featured on Home</span>
                </label>
              </div>
              <label>
                <span className="text-xs uppercase tracking-wider text-gray-500 block mb-1">Body (Markdown)</span>
                <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows="20" className={`${inputCls} font-mono text-xs`} data-testid="journal-body" />
                <span className="text-[11px] text-neutral-400 mt-1 block">{String(form.body || "").split(/\s+/).filter(Boolean).length.toLocaleString()} words</span>
              </label>
            </div>

            <div className="flex gap-2 mt-6 pt-4 border-t border-neutral-200">
              <button onClick={save} className="bg-[#001588] text-white px-6 py-2.5 text-sm hover:bg-black" data-testid="journal-save">Save changes</button>
              <button onClick={() => { setEditing(null); setForm(null); }} className="border border-gray-300 px-6 py-2.5 text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
